import { Controller } from "@hotwired/stimulus"

// How far down (px, or a quarter of the sheet if less) or how fast (px/ms) a drag must
// end for the sheet to close instead of springing back.
const DISMISS_DISTANCE = 160
const DISMISS_VELOCITY = 0.5
const SETTLE_MS = 200

// Opens the Ask AI chat (courses/ai_chats/_launcher). On the answered screen the chat
// takes the card's place from sm up and is a bottom sheet on phones; on the results
// page every question's button points the one modal's frame at its own chat (a bottom
// sheet on phones too). A sheet or modal locks the page scroll and closes on Escape;
// a phone's sheet also closes when dragged down by its top (the handle, the header and
// the question strip, marked data-ai-chat-grip) or by its reply list scrolled to the top.
export default class extends Controller {
    static targets = ["panel", "frame", "trigger", "sheet", "backdrop"]
    static values = { modal: Boolean }

    // A page Turbo restored from its cache (back from a law page the chat linked to,
    // ai-chat#openLink) comes back with the chat open, as it was left.
    connect() {
        if (!this.hasPanelTarget || this.panelTarget.classList.contains("hidden")) return

        this.overlay = this.modalValue || !this.wide
        if (this.overlay) document.body.classList.add("overflow-hidden")
    }

    get wide() {
        return window.matchMedia("(min-width: 640px)").matches
    }

    open({ currentTarget, params: { src } }) {
        if (!this.hasPanelTarget || !this.hasFrameTarget) return

        this.opener = currentTarget
        // The frame keeps its src as an absolute URL: a reopen must not reload the chat.
        const current = this.frameTarget.getAttribute("src")
        if (!current || new URL(current, location.href).href !== new URL(src, location.href).href) {
            this.frameTarget.src = src
        }
        this.panelTarget.classList.remove("hidden")
        this.overlay = this.modalValue || !this.wide
        if (this.overlay) {
            document.body.classList.add("overflow-hidden")
        } else {
            this.triggerTargets.forEach((trigger) => trigger.classList.add("hidden"))
            this.reveal()
            // The chat is taller than its loading state: bring it into view again once in.
            if (!this.frameTarget.complete) {
                this.frameTarget.addEventListener("turbo:frame-load", () => this.reveal(), { once: true })
            }
        }
    }

    reveal() {
        this.panelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    close() {
        if (!this.hasPanelTarget || this.panelTarget.classList.contains("hidden")) return

        this.panelTarget.classList.add("hidden")
        this.resetDrag()
        this.triggerTargets.forEach((trigger) => trigger.classList.remove("hidden"))
        if (this.overlay) document.body.classList.remove("overflow-hidden")
        this.opener?.focus({ preventScroll: true })
    }

    closeOnEscape(event) {
        if (event.key === "Escape" && this.overlay && !document.querySelector("#report-mistake-modal:not(.hidden)")) this.close()
    }

    dragStart(event) {
        this.drag = null
        if (!this.overlay || this.wide || event.touches.length !== 1) return

        const grip = event.target.closest("[data-ai-chat-grip]")
        const list = event.target.closest('[data-ai-chat-target~="messages"]')
        if (!grip && !list) return

        const { clientX, clientY } = event.touches[0]
        this.drag = { list, startX: clientX, startY: clientY, y: 0, velocity: 0, at: event.timeStamp, active: false }
    }

    // The first move decides: down from the grip, or down from a list already at its top,
    // drags the sheet; anything else is left to scroll.
    dragMove(event) {
        const drag = this.drag
        if (!drag) return

        const { clientX, clientY } = event.touches[0]
        const dy = clientY - drag.startY
        if (!drag.active) {
            const dx = clientX - drag.startX
            if (dy <= 0 || Math.abs(dx) > dy || (drag.list && drag.list.scrollTop > 0)) {
                this.drag = null
                return
            }
            drag.active = true
            this.sheetTarget.style.transition = "none"
            if (this.hasBackdropTarget) this.backdropTarget.style.transition = "none"
        }

        event.preventDefault()
        const y = Math.max(0, dy)
        const elapsed = event.timeStamp - drag.at
        if (elapsed > 0) drag.velocity = (y - drag.y) / elapsed
        drag.y = y
        drag.at = event.timeStamp
        this.sheetTarget.style.translate = `0 ${y}px`
        if (this.hasBackdropTarget) {
            this.backdropTarget.style.opacity = String(Math.max(0, 1 - y / this.sheetTarget.offsetHeight))
        }
    }

    dragEnd() {
        const drag = this.drag
        this.drag = null
        if (!drag?.active) return

        const sheet = this.sheetTarget
        const dismiss = drag.y > Math.min(DISMISS_DISTANCE, sheet.offsetHeight / 4) || drag.velocity > DISMISS_VELOCITY
        sheet.style.transition = `translate ${SETTLE_MS}ms ease-out`
        sheet.style.translate = dismiss ? "0 100%" : "0 0"
        if (this.hasBackdropTarget) {
            this.backdropTarget.style.transition = `opacity ${SETTLE_MS}ms ease-out`
            this.backdropTarget.style.opacity = dismiss ? "0" : "1"
        }
        clearTimeout(this.settleTimer)
        this.settleTimer = setTimeout(() => (dismiss ? this.close() : this.resetDrag()), SETTLE_MS)
    }

    resetDrag() {
        clearTimeout(this.settleTimer)
        const elements = [this.hasSheetTarget && this.sheetTarget, this.hasBackdropTarget && this.backdropTarget]
        elements.filter(Boolean).forEach((element) => {
            element.style.removeProperty("translate")
            element.style.removeProperty("transition")
            element.style.removeProperty("opacity")
        })
    }

    disconnect() {
        clearTimeout(this.settleTimer)
        if (this.overlay && this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")) {
            document.body.classList.remove("overflow-hidden")
        }
    }
}
